import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar, StatCard, GridCard, ListCard, Modal } from "@/components";
import { useClosetStore, useUserStore } from "@/store";
import { MEMBERS, MEMBER_NAMES, SAMPLE_ITEMS } from "@/data/sample";
import obStyles from "@/pages/Onboarding/Onboarding.module.css";
import styles from "./ClosetPage.module.css";
import EmptyGridCard from "@/components/Card/EmptyGridCard";
import EmptyListCard from "@/components/Card/EmptyListCard";
import { ClothingItem } from "@/types/product";
import Toolbar from "@/components/Toolbar/ToolBar";
import ShareModal from "@/components/Sidebar/ShareModal";
import { trackItemViewed, trackShareModalOpened, trackMemberSelected, trackCategoryFiltered, trackLogoutConfirmed } from "@/services/analytics";

const PAGE_SIZE = 12;

export function ClosetPage() {
  const navigate = useNavigate();
  const { syncStatus, syncProgress, logout, user, pollSyncProgress, completeOnboarding } = useUserStore();
  const {
    items,
    activeMemberId,
    activeCategoryName,
    viewMode,
    setActiveMember,
    toggleCategory,
    reset: resetCloset,
    getClosetItems,
    loadingInitialPage,
    loadingNextPage,
    filter
  } = useClosetStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [sharingModalOpen, setSharingModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ClothingItem | null>(null);

  const syncing = syncStatus === "syncing";

  const containerRef = useRef<HTMLDivElement>(null);
  const hasMore = filter.next_page !== null && filter.next_page !== undefined;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !loadingNextPage) {
        getClosetItems(
          { page: filter.next_page!, page_size: PAGE_SIZE },
          true
        );
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingNextPage, filter.next_page]);

  useEffect(() => {
    getClosetItems({ page: 1, page_size: PAGE_SIZE });
    pollSyncProgress();

    // Mark onboarding as complete when user first reaches the closet
    if (!user?.completed_onboarding_at) {
      completeOnboarding();
    }
  }, []);

  // Stats
  const totalValue = user?.total_value ? Math.round(Number(user.total_value)) : "";
  const uniqueBrands = user?.brands_count || 0;
  const avaragePrice = user?.average_product_price ? Math.round(Number(user.average_product_price)) : "";
  const showMemberBadge = activeMemberId === "all";

  function handleItemClick(item: ClothingItem) {
    trackItemViewed({
      id: item.id,
      name: item.name,
      brand: item.brand?.name,
      category: item.category_name,
      price: item.price_paid,
    });
    setSelectedItem(item);
    setModalOpen(true);
  }

  function handleMemberSelect(memberId: string) {
    trackMemberSelected(memberId);
    setActiveMember(memberId);
  }

  function handleCategorySelect(categoryName: string) {
    trackCategoryFiltered(categoryName);
    toggleCategory(categoryName);
  }

  function handleShareOpen() {
    trackShareModalOpened();
    setSharingModalOpen(true);
  }

  function handleLogout() {
    trackLogoutConfirmed();
    logout();
    resetCloset();
    navigate("/onboarding");
  }

  return (
    <div className={styles.app}>
      <Sidebar
        members={MEMBERS}
        categories={[]}
        activeMemberId={activeMemberId}
        activeCategoryName={activeCategoryName}
        onMemberSelect={handleMemberSelect}
        onCategorySelect={handleCategorySelect}
        onAddMember={() => {}}
        onShare={handleShareOpen}
        disabled={!user?.inventory_id}
        onLogout={handleLogout}
      />

      <div ref={containerRef} className={styles.container}>
        {/* Sync banner — only visible while a sync is running */}
        {syncing && (
          <div className={styles.syncHeader}>
            <div className={obStyles.syncBannerRing} />
            <div className={obStyles.syncBannerText}>
              Syncing your purchases...
            </div>
            <div className={obStyles.syncBannerBar}>
              <div className={obStyles.syncBannerFill} style={{ width: `${Math.round(syncProgress)}%` }} />
            </div>
            <div className={obStyles.syncBannerPct}>{Math.round(syncProgress)}%</div>
          </div>
        )}

        {/* Header */}
        <div className={styles.header} style={syncing ? { top: 37 } : undefined}>
          <div className={styles.headerTitle}>
            {activeMemberId === "all" ? "All" : MEMBER_NAMES[activeMemberId] || activeMemberId}
          </div>
        </div>

        <div className={styles.main} style={syncing ? { paddingTop: 101 } : undefined}>

          {/* Stats */}
          <div className={styles.statsWrap}>
            <div className={styles.statsGrid}>
              <StatCard value={String(user?.products_count || 0)} label="Items" />
              <StatCard value={String(uniqueBrands)} label="Brands" />
              <StatCard value={"$" + avaragePrice} label="Average price per item" />
              <StatCard value={"$" + totalValue} label="Total Value" />
            </div>
          </div>

          {/* Toolbar */}
          <Toolbar />

          {/* Content */}
          <div className={styles.content}>
            {items.length === 0 && !loadingInitialPage ? (
              <div className={styles.empty}>
                <h3>No items found</h3>
                <p>Try adjusting your search or filters.</p>
              </div>
            ) : viewMode === "grid" ? (
              <div className={styles.gridView}>
                {!loadingInitialPage
                  ? items.map((item) => (
                      <GridCard
                        key={item.id}
                        item={item}
                        showMemberBadge={showMemberBadge}
                        onClick={() => handleItemClick(item)}
                      />
                    ))
                  : SAMPLE_ITEMS.map((item) => <EmptyGridCard key={item.id} />)}
              </div>
            ) : (
              <div className={styles.listView}>
                {!loadingInitialPage
                  ? items.map((item) => <ListCard key={item.id} item={item} onClick={() => handleItemClick(item)} />)
                  : SAMPLE_ITEMS.map((item) => <EmptyListCard key={item.id} />)}
              </div>
            )}

            {/* Bottom loader while fetching next page */}
            {loadingNextPage && (
              <div className={styles.loadingMore}>
                <div className={styles.loadingMoreSpinner} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Item Detail Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={selectedItem?.brand.name ?? ""}>
        {selectedItem && (
          <>
            <img
              className={styles.modalImage}
              src={
                selectedItem?.product_images[0].image.large_image ||
                selectedItem?.product_images[0].image.original_image ||
                ""
              }
            />
            <div className={styles.modalGrid}>
              <DetailField label="Product" value={selectedItem.name} />
              <DetailField label="Brand" value={selectedItem.brand?.name} />
              {selectedItem.category_name && (
                <DetailField
                  label="Category"
                  value={`${selectedItem.category_name} / ${selectedItem.sub_category_name}`}
                />
              )}
              <DetailField label="Price" value={`$${selectedItem.price_paid}`} />
              {/* <DetailField label="Color" value={selectedItem} /> */}
              <DetailField label="Size" value={selectedItem.size} />
              <DetailField label="Purchased" value={formatDateToYYYYMMDD(new Date(selectedItem.purchased_at || ""))} />
              <DetailField label="Retailer" value={selectedItem.retailer?.name || "(no retailer)"} />
              {/* <DetailField label="Family" value={MEMBER_NAMES[selectedItem.member] || selectedItem.member} /> */}
            </div>
          </>
        )}
      </Modal>
      <ShareModal visibility={sharingModalOpen} onChange={setSharingModalOpen} />
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailField}>
      <label className={styles.detailLabel}>{label}</label>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}
